import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { formatUsd } from '@/features/rewards/lib/profileStepUtils'
import { useCountUp } from '@/shared/hooks/useCountUp'
import {
  homeStatCardSx,
  homeStatLabel,
  homeStatValue,
} from '../styles/homeStyles'

export type HomeStatValueFormat = 'currency' | 'count'

function formatAnimatedValue(animated: number, valueFormat: HomeStatValueFormat): string {
  if (valueFormat === 'currency') return formatUsd(animated)
  return String(Math.round(animated))
}

interface HomeStatCardProps {
  readonly label: string
  /** Raw metric — animated from 0 on load / when value changes. */
  readonly value: number
  readonly valueFormat?: HomeStatValueFormat
  readonly iconBg: string
  readonly icon: ReactNode
  readonly cardBg: string
  readonly testId?: string
}

export function HomeStatCard({
  label,
  value,
  valueFormat = 'count',
  iconBg,
  icon,
  cardBg,
  testId,
}: HomeStatCardProps) {
  const animated = useCountUp(Number.isFinite(value) ? value : 0, 700, valueFormat === 'currency' ? 2 : 0)
  const display = formatAnimatedValue(animated, valueFormat)

  return (
    <Box
      sx={{ ...homeStatCardSx, bgcolor: cardBg, backgroundColor: cardBg }}
      data-testid={testId}
    >
      <Box>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '21px',
            color: homeStatLabel,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            mt: 1,
            fontSize: '18px',
            fontWeight: 600,
            lineHeight: '27px',
            color: homeStatValue,
          }}
        >
          {display}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '10px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          flexShrink: 0,
        }}
        aria-hidden
      >
        {icon}
      </Box>
    </Box>
  )
}
